/**
 * Shared "are you sure?" confirmation modal for answers that route the
 * questionnaire flow to the ``ineligible`` terminus.
 *
 * Both the self-serve pre-screen page and the clinic-side
 * ``QuestionnaireStepPanel`` need the same UX: BEFORE submitting an
 * answer that ``Question.next_questions`` maps to ``null`` (the
 * traverser's ineligible marker), pop a one-shot modal so the
 * participant can either confirm or step back and revise. Centralising
 * this here keeps the predicate (which boolean key conventions to try,
 * how to fall back to "null"/"any") in one place — previously only the
 * pre-screen page had it, so the clinic flow silently submitted
 * disqualifying answers without any guard rail.
 *
 * The predicate mirrors ``QuestionFlowTraverser.get_next_question_target``
 * on the backend so the modal fires for exactly the routings that
 * would land the participant at INELIGIBLE. Anything else
 * (``-1`` -> complete, an integer/string -> next question) returns
 * false here; the caller can submit straight through.
 *
 * Note: this module is purely client-side UX. The backend's
 * ``submit_questionnaire_answer`` is the authoritative source of
 * truth — even if the modal is bypassed (or mis-wired by a caller),
 * the backend still pins INELIGIBLE on a routing-to-null answer.
 * The modal is just a friendlier confirmation step.
 */
import React from 'react';
import { OnyxConfirmModal } from '../onyx/OnyxConfirmModal';

/**
 * Look up the next-question target a given answer would take, using
 * the same key-resolution order the backend traverser uses (exact
 * match -> "Yes"/"No" -> "true"/"false" -> "null" -> "any"). Returns
 * the raw value from ``next_questions`` so callers can distinguish
 * "ineligible" (``null``) from "complete" (``-1``) from "next
 * question" (anything else).
 */
const resolveNextRouting = (question, answerValue) => {
  const map = question?.next_questions;
  if (!map) return undefined;

  const tryKey = (key) =>
    Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined;

  let value = tryKey(String(answerValue));
  if (value !== undefined) return value;

  if (typeof answerValue === 'boolean') {
    value = tryKey(answerValue ? 'Yes' : 'No');
    if (value !== undefined) return value;
    value = tryKey(answerValue ? 'yes' : 'no');
    if (value !== undefined) return value;
    value = tryKey(answerValue ? 'true' : 'false');
    if (value !== undefined) return value;
  }

  value = tryKey('null');
  if (value !== undefined) return value;
  return tryKey('any');
};

/**
 * Returns true iff submitting ``answerValue`` for ``question`` would
 * route the participant to the flow's ineligible terminus.
 *
 * Use this BEFORE calling submit so the caller can show the
 * confirmation modal and let the participant back out without
 * persisting the answer (which would land them at INELIGIBLE on the
 * backend, sticky thereafter for self-serve users).
 */
export const wouldAnswerBeIneligible = (question, answerValue) => {
  const routing = resolveNextRouting(question, answerValue);
  return routing === null;
};

/**
 * Standard "Are you sure?" modal for ineligible-routing answers.
 * Body copy is intentionally generic so the same modal serves both
 * self-serve participants and the CSC iPad flow — which framing the
 * caller wants ("you may not be eligible" vs "this answer will mark
 * the participant ineligible") can be customised via ``message``.
 */
export const IneligibleConfirmationModal = ({
  open,
  onCancel,
  onConfirm,
  title = 'Confirm Your Answer',
  message,
}) => (
  <OnyxConfirmModal
    open={open}
    onClose={onCancel}
    onConfirm={onConfirm}
    title={title}
    cancelLabel="Review Answer"
    confirmLabel="Confirm and Submit"
  >
    <p className="prescreen-confirm-body">
      {message ||
        'This answer indicates the participant may not be eligible for this study. Are you sure you want to submit?'}
    </p>
  </OnyxConfirmModal>
);

export default IneligibleConfirmationModal;
